import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Textarea } from '@ouropix/ui';
import { Switch } from '@ouropix/ui';
import { Separator } from '@ouropix/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@ouropix/ui';
import { toast } from 'sonner';
import {
  Globe,
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  BotIcon,
  Image as ImageIcon,
  Code2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MediaLibraryModal } from '@/components/media/MediaLibraryModal';

export const Route = createFileRoute('/_admin/seo/$slug')({
  component: SeoEditorPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Section { h2: string; html: string }
interface FaqItem { question: string; answer: string }

interface SeoFormData {
  page_slug: string;
  page_type: string;
  category: string;
  title: string;
  description: string;
  keywords: string;
  og_image_url: string;
  og_type: string;
  canonical_url: string;
  robots: string;
  schema_org: string;
  h1: string;
  intro: string;
  sections: Section[];
  faq: FaqItem[];
  body_html: string;
  video_url: string;
  agent_description: string;
  is_active: boolean;
  publish: boolean;
  published_at: string;
}

const EMPTY_FORM: SeoFormData = {
  page_slug:         '',
  page_type:         'static',
  category:          '',
  title:             '',
  description:       '',
  keywords:          '',
  og_image_url:      '',
  og_type:           'website',
  canonical_url:     '',
  robots:            'index, follow',
  schema_org:        '',
  h1:                '',
  intro:             '',
  sections:          [],
  faq:               [],
  body_html:         '',
  video_url:         '',
  agent_description: '',
  is_active:         true,
  publish:           false,
  published_at:      '',
};

const CATEGORIES = ['Home', 'Jogos', 'Promoções', 'Compliance', 'Cadastro'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function CharCounter({ value, max, warn }: { value: string; max: number; warn?: number }) {
  const len = value.length;
  const isWarn = warn ? len > warn : false;
  const isOver = len > max;
  return (
    <span className={cn('text-xs', isOver ? 'text-destructive' : isWarn ? 'text-amber-500' : 'text-muted-foreground')}>
      {len}/{max}
    </span>
  );
}

function GooglePreview({ title, slug, description }: { title: string; slug: string; description: string }) {
  return (
    <div className="rounded-lg border bg-white p-4 font-sans text-sm shadow-sm dark:bg-card">
      <p className="text-xs text-muted-foreground mb-1">Pré-visualização Google</p>
      <Separator className="mb-3" />
      <p className="text-blue-700 dark:text-blue-400 text-base leading-tight line-clamp-2">
        {title || 'Título da página...'}
      </p>
      <p className="text-green-700 dark:text-green-500 text-xs mt-0.5">
        ouropix.com.br{slug}
      </p>
      <p className="text-gray-600 dark:text-muted-foreground text-xs mt-1 line-clamp-2">
        {description || 'Descrição que aparece nos resultados de busca do Google...'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function SeoEditorPage() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();

  // Decodifica slug: "_root" → "/" ; outros → "/<slug>" (__ = separador de sub-path)
  const pageSlug = slug === '_root' ? '/' : `/${slug.replace(/__/g, '/')}`;

  const [form, setForm] = useState<SeoFormData>({ ...EMPTY_FORM, page_slug: pageSlug });
  const [loaded, setLoaded] = useState(false);
  const [showAgentPanel, setShowAgentPanel] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);

  const { data: config, isLoading } = useQuery<any>({
    queryKey: ['admin-seo-detail', pageSlug],
    queryFn: async () => {
      const encoded = encodeURIComponent(pageSlug.replace(/^\//, '') || '_root');
      try {
        const res = await api.get(`/admin/seo/${encoded}`);
        return res.data;
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
  });

  useEffect(() => {
    if (config !== undefined && !loaded) {
      setLoaded(true);
      if (!config) return; // 404 — manter form vazio com slug preenchido
      let sectionsArr: Section[] = [];
      let faqArr: FaqItem[] = [];
      try { sectionsArr = config.sections ? JSON.parse(config.sections) : []; } catch { /* ok */ }
      try { faqArr      = config.faq      ? JSON.parse(config.faq)      : []; } catch { /* ok */ }
      setForm({
        page_slug:         config.page_slug        ?? pageSlug,
        page_type:         config.page_type        ?? 'static',
        category:          config.category         ?? '',
        title:             config.title            ?? '',
        description:       config.description      ?? '',
        keywords:          config.keywords         ?? '',
        og_image_url:      config.og_image_url     ?? '',
        og_type:           config.og_type          ?? 'website',
        canonical_url:     config.canonical_url    ?? '',
        robots:            config.robots           ?? 'index, follow',
        schema_org:        config.schema_org       ?? '',
        h1:                config.h1               ?? '',
        intro:             config.intro            ?? '',
        sections:          sectionsArr,
        faq:               faqArr,
        body_html:         config.body_html        ?? '',
        video_url:         config.video_url        ?? '',
        agent_description: config.agent_description ?? '',
        is_active:         config.is_active        !== false,
        publish:           !!config.published_at,
        published_at:      config.published_at     ?? '',
      });
    }
  }, [config, loaded, pageSlug]);

  const upsertMutation = useMutation({
    mutationFn: async (payload: SeoFormData) => {
      const encoded = encodeURIComponent(payload.page_slug.replace(/^\//, '') || '_root');
      await api.put(`/admin/seo/${encoded}`, {
        page_type:         payload.page_type,
        category:          payload.category     || undefined,
        title:             payload.title,
        description:       payload.description,
        keywords:          payload.keywords,
        og_image_url:      payload.og_image_url,
        og_type:           payload.og_type,
        canonical_url:     payload.canonical_url,
        robots:            payload.robots,
        schema_org:        payload.schema_org ? JSON.parse(payload.schema_org) : undefined,
        h1:                payload.h1           || undefined,
        intro:             payload.intro        || undefined,
        sections:          payload.sections,
        faq:               payload.faq,
        body_html:         payload.body_html    || undefined,
        video_url:         payload.video_url    || undefined,
        agent_description: payload.agent_description || undefined,
        is_active:         payload.is_active,
        publish:           payload.publish,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seo-list-all'] });
      queryClient.invalidateQueries({ queryKey: ['admin-seo-detail', pageSlug] });
      setLoaded(false); // force reload
      toast.success('Configuração SEO salva!');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Erro ao salvar configuração SEO');
    },
  });

  const handleSave = () => {
    if (form.schema_org) {
      try { JSON.parse(form.schema_org); }
      catch { toast.error('Schema.org deve ser JSON válido'); return; }
    }
    upsertMutation.mutate(form);
  };

  const addSection = () => setForm((p) => ({ ...p, sections: [...p.sections, { h2: '', html: '' }] }));
  const removeSection = (i: number) => setForm((p) => ({ ...p, sections: p.sections.filter((_, idx) => idx !== i) }));
  const updateSection = (i: number, field: keyof Section, value: string) => {
    setForm((p) => {
      const next = [...p.sections];
      next[i] = { ...next[i], [field]: value };
      return { ...p, sections: next };
    });
  };

  const addFaq = () => setForm((p) => ({ ...p, faq: [...p.faq, { question: '', answer: '' }] }));
  const removeFaq = (i: number) => setForm((p) => ({ ...p, faq: p.faq.filter((_, idx) => idx !== i) }));
  const updateFaq = (i: number, field: keyof FaqItem, value: string) => {
    setForm((p) => {
      const next = [...p.faq];
      next[i] = { ...next[i], [field]: value };
      return { ...p, faq: next };
    });
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/seo">
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <h1 className="text-xl font-bold">Editar Página SEO</h1>
            </div>
            <code className="text-xs text-muted-foreground font-mono">{pageSlug}</code>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/seo">
            <Button variant="outline">Cancelar</Button>
          </Link>
          <Button onClick={handleSave} disabled={upsertMutation.isPending} className="gap-2">
            <Save className="h-4 w-4" />
            {upsertMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground">Carregando...</div>
      ) : (
        <div className="space-y-6">
          {/* Meta info */}
          <div className="rounded-lg border bg-card p-5 space-y-4">
            <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Informações da Página
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Marca</Label>
                <Input value="OuroPix" disabled className="bg-muted text-muted-foreground" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-category">Pasta (categoria)</Label>
                <select
                  id="seo-category"
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                >
                  <option value="">Sem categoria</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Slug</Label>
              <Input value={pageSlug} disabled className="bg-muted text-muted-foreground font-mono text-sm" />
            </div>
          </div>

          {/* Agent SEO panel (placeholder) */}
          <div className="rounded-lg border bg-card p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BotIcon className="h-4 w-4 text-muted-foreground" />
                <Label className="cursor-pointer font-semibold">Usar Agent SEO</Label>
                <Badge variant="outline" className="text-xs">Wave E</Badge>
              </div>
              <Switch
                checked={showAgentPanel}
                onCheckedChange={setShowAgentPanel}
              />
            </div>
            {showAgentPanel && (
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label htmlFor="seo-agent-desc">Descrição da página (contexto para o agent)</Label>
                  <Textarea
                    id="seo-agent-desc"
                    value={form.agent_description}
                    rows={3}
                    onChange={(e) => setForm((p) => ({ ...p, agent_description: e.target.value }))}
                    placeholder="Descreva o propósito desta página para o agent de SEO..."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="seo-video-url">URL do vídeo (opcional)</Label>
                  <Input
                    id="seo-video-url"
                    value={form.video_url}
                    onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
                <Button className="gap-2 w-full" onClick={() => setShowAgentModal(true)}>
                  <BotIcon className="h-4 w-4" />
                  Gerar SEO/GEO completo
                </Button>
              </div>
            )}
          </div>

          {/* SEO básico */}
          <div className="rounded-lg border bg-card p-5 space-y-4">
            <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              SEO Básico
            </h2>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="seo-title">Meta Title</Label>
                  <p className="text-xs text-muted-foreground">Aparece na aba do browser e nos resultados do Google (ideal: 50–60 chars)</p>
                </div>
                <CharCounter value={form.title} max={70} warn={60} />
              </div>
              <Input
                id="seo-title"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="OuroPix — Loteria Digital (50–60 chars ideal)"
                maxLength={70}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="seo-desc">Meta Description</Label>
                <CharCounter value={form.description} max={160} warn={140} />
              </div>
              <Textarea
                id="seo-desc"
                value={form.description}
                rows={3}
                maxLength={160}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Participe de jogos de loteria digital seguros e transparentes..."
              />
            </div>

            <GooglePreview
              title={form.title}
              slug={pageSlug}
              description={form.description}
            />

            <div className="space-y-1.5">
              <Label htmlFor="seo-keywords">Keywords (CSV)</Label>
              <Input
                id="seo-keywords"
                value={form.keywords}
                onChange={(e) => setForm((p) => ({ ...p, keywords: e.target.value }))}
                placeholder="loteria, sorteio, online"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="seo-robots">Robots</Label>
                <select
                  id="seo-robots"
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.robots}
                  onChange={(e) => setForm((p) => ({ ...p, robots: e.target.value }))}
                >
                  <option value="index, follow">index, follow</option>
                  <option value="noindex, nofollow">noindex, nofollow</option>
                  <option value="noindex, follow">noindex, follow</option>
                  <option value="index, nofollow">index, nofollow</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-canonical">Canonical URL</Label>
                <Input
                  id="seo-canonical"
                  value={form.canonical_url}
                  onChange={(e) => setForm((p) => ({ ...p, canonical_url: e.target.value }))}
                  placeholder="https://ouropix.com.br/"
                />
              </div>
            </div>
          </div>

          {/* Conteúdo editorial */}
          <div className="rounded-lg border bg-card p-5 space-y-4">
            <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Conteúdo Editorial (GEO/SEO rico)
            </h2>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="seo-h1">H1</Label>
                <CharCounter value={form.h1} max={120} warn={80} />
              </div>
              <Input
                id="seo-h1"
                value={form.h1}
                onChange={(e) => setForm((p) => ({ ...p, h1: e.target.value }))}
                placeholder="Título principal da página (H1)..."
                maxLength={120}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="seo-intro">Intro (lead paragraph)</Label>
                <CharCounter value={form.intro} max={25000} />
              </div>
              <Textarea
                id="seo-intro"
                value={form.intro}
                rows={4}
                onChange={(e) => setForm((p) => ({ ...p, intro: e.target.value }))}
                placeholder="Parágrafo de introdução exibido abaixo do H1..."
              />
            </div>

            {/* Seções */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Seções (H2 + conteúdo)</Label>
                <Button type="button" variant="outline" size="sm" className="gap-1 h-7" onClick={addSection}>
                  <Plus className="h-3.5 w-3.5" />
                  Adicionar seção
                </Button>
              </div>
              {form.sections.length === 0 && (
                <p className="text-xs text-muted-foreground">Nenhuma seção ainda.</p>
              )}
              {form.sections.map((sec, i) => (
                <div key={i} className="border rounded-md p-3 space-y-2 bg-muted/30">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground font-mono">#{i + 1}</span>
                    <Button
                      type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => removeSection(i)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Input
                    value={sec.h2}
                    onChange={(e) => updateSection(i, 'h2', e.target.value)}
                    placeholder="Título H2 da seção"
                    className="text-sm"
                  />
                  <Textarea
                    value={sec.html}
                    onChange={(e) => updateSection(i, 'html', e.target.value)}
                    rows={3}
                    placeholder="Conteúdo HTML da seção (tags: p, strong, em, ul, ol, li, a)"
                    className="text-sm font-mono"
                  />
                </div>
              ))}
            </div>

            {/* FAQ */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>FAQ (perguntas e respostas)</Label>
                <Button type="button" variant="outline" size="sm" className="gap-1 h-7" onClick={addFaq}>
                  <Plus className="h-3.5 w-3.5" />
                  Adicionar pergunta
                </Button>
              </div>
              {form.faq.length === 0 && (
                <p className="text-xs text-muted-foreground">Nenhuma pergunta ainda.</p>
              )}
              {form.faq.map((item, i) => (
                <div key={i} className="border rounded-md p-3 space-y-2 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-mono">Q{i + 1}</span>
                    <Button
                      type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => removeFaq(i)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Input
                    value={item.question}
                    onChange={(e) => updateFaq(i, 'question', e.target.value)}
                    placeholder="Pergunta..."
                    className="text-sm"
                  />
                  <Textarea
                    value={item.answer}
                    onChange={(e) => updateFaq(i, 'answer', e.target.value)}
                    rows={2}
                    placeholder="Resposta..."
                    className="text-sm"
                  />
                </div>
              ))}
            </div>

            {/* Body HTML */}
            <div className="space-y-1.5">
              <Label htmlFor="seo-body-html">
                Corpo HTML adicional (opcional)
                <span className="text-xs text-muted-foreground ml-1">
                  — tags permitidas: p, strong, em, ul, ol, li, a, h2, h3, br
                </span>
              </Label>
              <Textarea
                id="seo-body-html"
                value={form.body_html}
                rows={5}
                className="font-mono text-xs"
                onChange={(e) => setForm((p) => ({ ...p, body_html: e.target.value }))}
                placeholder="<p>Conteúdo adicional...</p>"
              />
            </div>
          </div>

          {/* OG Image */}
          <div className="rounded-lg border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                OG Image
              </h2>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seo-og-image">OG Image</Label>
              <div className="flex gap-2">
                <Input
                  id="seo-og-image"
                  value={form.og_image_url}
                  onChange={(e) => setForm((p) => ({ ...p, og_image_url: e.target.value }))}
                  placeholder="Cole uma URL ou selecione da biblioteca de mídia"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMediaLibraryOpen(true)}
                  className="gap-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  {form.og_image_url ? 'Trocar' : 'Selecionar'}
                </Button>
                {form.og_image_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setForm((p) => ({ ...p, og_image_url: '' }))}
                    title="Remover imagem"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {form.og_image_url && (
              <img
                src={form.og_image_url}
                alt="OG preview"
                className="rounded-md border max-h-40 object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <div className="space-y-1.5">
              <Label htmlFor="seo-og-type">OG Type</Label>
              <Input
                id="seo-og-type"
                value={form.og_type}
                onChange={(e) => setForm((p) => ({ ...p, og_type: e.target.value }))}
                placeholder="website"
              />
            </div>
          </div>

          {/* JSON-LD Schema */}
          <div className="rounded-lg border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                JSON-LD Schema
              </h2>
            </div>
            <Textarea
              value={form.schema_org}
              rows={8}
              className="font-mono text-xs"
              onChange={(e) => setForm((p) => ({ ...p, schema_org: e.target.value }))}
              placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "WebSite"\n}'}
            />
            <p className="text-xs text-muted-foreground">
              JSON válido — será injetado como <code>&lt;script type="application/ld+json"&gt;</code> na página.
            </p>
          </div>

          {/* Publicação */}
          <div className="rounded-lg border bg-card p-5 space-y-4">
            <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Publicação
            </h2>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="seo-active" className="cursor-pointer font-medium">
                  Configuração ativa
                </Label>
                <p className="text-xs text-muted-foreground">
                  Site usa estes metadados quando ativo
                </p>
              </div>
              <Switch
                id="seo-active"
                checked={form.is_active}
                onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: v }))}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="seo-publish" className="cursor-pointer font-medium">
                  Publicar página
                </Label>
                <p className="text-xs text-muted-foreground">
                  {form.published_at
                    ? `Publicada em ${new Date(form.published_at).toLocaleDateString('pt-BR')}`
                    : 'Define published_at e marca como publicada'}
                </p>
              </div>
              <Switch
                id="seo-publish"
                checked={form.publish}
                onCheckedChange={(v) => setForm((p) => ({ ...p, publish: v }))}
              />
            </div>
          </div>

          {/* Salvar rodapé */}
          <div className="flex justify-end gap-2 pb-8">
            <Link to="/seo">
              <Button variant="outline">Cancelar</Button>
            </Link>
            <Button onClick={handleSave} disabled={upsertMutation.isPending} className="gap-2">
              <Save className="h-4 w-4" />
              {upsertMutation.isPending ? 'Salvando...' : 'Salvar Configuração'}
            </Button>
          </div>
        </div>
      )}

      {/* Modal informativo — Agent IA em integração */}
      <Dialog open={showAgentModal} onOpenChange={setShowAgentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BotIcon className="h-5 w-5 text-primary" />
              Otimizar com Agente em Tempo Real
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed">
              O Agente de SEO/GEO IA está em integração final. Em breve você vai poder gerar
              título, meta, H1, intro, seções, FAQ e JSON-LD automaticamente a partir de uma
              descrição livre da página. Por agora, edite os campos manualmente abaixo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowAgentModal(false)}>Entendi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaLibraryModal
        open={mediaLibraryOpen}
        onOpenChange={setMediaLibraryOpen}
        onSelect={(url) => setForm((p) => ({ ...p, og_image_url: url }))}
        selectedUrl={form.og_image_url}
      />
    </div>
  );
}
